<template>
    <div v-if="tfas">
        <div v-if="step == 1" >
            <h2 class="style-h2 mb-4">
                2FA Security
            </h2>

            <div class="style-body mb-4">
                Protect your account from suspicious activity by adding Two Factor Authentication into your sign-in.
            </div>

            <div class="style-body border-t border-b border-gray-100 divide-y divide-gray-100 mb-5">
                <div v-if="tfas.email" class="grid grid-cols-10 w-full py-4 items-center">
                    <div class="col-span-5">
                        Email Verification
                    </div>
                    <div class="col-span-5">
                        <i class="fal fa-check-circle text-success mr-2 text-lg leading-none"></i> Verified
                    </div>
                </div>

                <div v-if="tfas.google_authenticator" class="grid grid-cols-10 w-full py-4 items-center">
                    <div class="col-span-5">
                        Authenticator App
                    </div>
                    <div class="col-span-5">
                        <div v-if="tfas.google_authenticator.status == 'Enable'" class="cursor-pointer flex" @click="step = 5">
                            <i class="fal fa-check-circle text-success mr-2 text-lg leading-none"></i> Verified <span class="inline-block ml-auto"><i class="far fa-chevron-right"></i></span>
                        </div>
                        <div v-if="tfas.google_authenticator.status == 'Pending'" class="cursor-pointer flex" @click="step = 2">
                            <i class="fal fa-times-circle text-red mr-2 text-lg leading-none"></i> Requires Setup <span class="inline-block ml-auto"><i class="far fa-chevron-right"></i></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="success">
            <div class="py-12 ">
                <div>
                    <i class="fas fa-check-circle text-6xl text-success"></i>
                </div>
                <div class="text-xl mt-5">
                    Success!
                </div>
                <div class="style-body mt-5">
                    <p class="mb-2">
                        You’ve successfully setup Two Factor Authentication.
                    </p>
                    <p>
                        We’ll ask for a code from your Authenticator app when login.
                    </p>
                </div>

                <div class="mt-8">
                    <button class="btn" @click="goBackToStep1">Return</button>
                </div>
            </div>
        </div>
        <div v-else>
            <div v-if="step == 2 || step == 3" >
                <h2 class="style-h2 mb-2">
                    Authenticator App
                </h2>

                <div class="style-body mb-4">
                    Use an app such as Google Authenticator to generate verification codes prior to login for more protection.
                </div>

                <div v-if="step == 2" class="border-t border-gray-100 pt-4">
                    <div class="style-body mb-4">
                        Scan the QR Code when instructed by your Authenticator App.
                    </div>
                    <div class="mb-4">
                        <img :src="get2FAQrCodeUrl()" class="max-w-xs"/>
                    </div>
                    <div class="flex items-center">
                        <button class="btn" @click="step = 3">Continue</button>
                        <span
                                @click="step = 1"
                                class="ml-4 text-red hover:text-red-hover font-light underline cursor-pointer">Return</span>
                    </div>
                </div>

                <div v-if="step == 3" class="border-t border-gray-100 pt-4">
                    <div class="style-body mb-4">
                        Enter the 6 Digit code provided by your app.
                    </div>

                    <form
                            action=""
                            class="form form--floating-label"
                            @submit.prevent="() => formSubmit('google_authenticator')"
                            ref='form'>

                        <div class="input-container input-container--floating relative h-12 mb-5 mt-4" :class="{empty: !code , error: submittedOnce && errors.code}">
                            <input
                                    id="code"
                                    type="number"
                                    v-model="code"
                                    class="h-full w-full px-2 transition-all border-blue-gray rounded text-blue"
                            />
                            <label for="code" class="absolute left-2 transition-all px-1 text-blue-gray">
                                6 Digit Code
                            </label>
                        </div>

                        <div v-if="submittedOnce && errors.message" class="bg-red text-white mt-10 mb-5 px-3 py-2 rounded ">
                            {{ errors.message }}
                        </div>

                        <div class="flex items-center">
                            <button type="submit" class="btn w-full md:w-auto">Submit</button>
                            <span
                                    @click="step = 2"
                                    class="ml-4 text-red hover:text-red-hover font-light underline cursor-pointer">Return</span>
                        </div>
                    </form>
                </div>
            </div>
        </div>


        <div v-if="step == 5">
            <h2 class="style-h2 mb-2">
                Authenticator App
            </h2>

            <div class="style-body mb-4">
                Use an app such as Google Authenticator to generate verification codes prior to login for more protection.
            </div>

            <div class="mt-6 flex items-center">
                <button class="btn btn--gray-outline"
                        @click="() => disable('google_authenticator')" >Disable App</button>
                <span
                        @click="step = 1"
                        class="ml-4 text-red hover:text-red-hover font-light underline cursor-pointer">Return</span>
            </div>
        </div>
    </div>
</template>

<script charset="utf-8">

import { values, join, get, assign, pick } from 'lodash';
import Vuex from 'vuex';

export default {
    name: 'TfaForm',
    props: [],
    components: {

    },

    data() {
        return {
            step: 1,
            hideCodeInput: true,
            code: '',
            tfa: null,

            loading: false,
            submittedOnce: false,
            success: false,
            'errors': {
                code: '',
            }
        }
    },

    emits: ["submit-success"],

    created() {
        this.tfa = this.tfas.google_authenticator;
    },

    computed: {
        ...Vuex.mapGetters('profile', {
            profile: 'profile',
            tfas: 'tfas'
        }),
    },

    methods: {
        ...Vuex.mapActions('profile', {
            verifyUser2FA: 'verifyUser2FA',
            updateUser2FA: 'updateUser2FA',
        }),
        ...Vuex.mapMutations('profile', {
            updateUser: 'updateUser'
        }),



        formSubmit(type) {
            this.submittedOnce = true;
            this.errors.message = '';

            let tfa = assign({}, this.tfa, {
                code: this.code,
            });

            this.verifyUser2FA(tfa).then(res => {
                if (type == 'google_authenticator') {
                    let temp = JSON.parse(JSON.stringify(this.tfas));
                    temp.google_authenticator = res;
                    let newTfas = {
                        tfas: temp
                    };
                    this.updateUser(newTfas);
                }
                this.success = true;
                this.$emit('submit-success');
            })
            .catch(err => {
                this.errors = {
                    message: get(err, 'response.data.message')
                }
            })
        },

        disable(type){
            let tfa = assign({}, this.tfa, {
                status: 'Pending',
            });

            this.updateUser2FA(tfa).then(res => {
                if (type == 'google_authenticator') {
                    let temp = JSON.parse(JSON.stringify(this.tfas));
                    temp.google_authenticator = res;
                    let newTfas = {
                        tfas: temp
                    };
                    this.updateUser(newTfas);
                }
                this.step = 1;
            })
            .catch(err => {
                this.errors = {
                    message: get(err, 'response.data.message')
                }
            })
        },

        goBackToStep1(){
            this.step = 1;
            this.success = false;
        },

        closeModal() {

        },

        get2FAQrCodeUrl() {
            return window.config.apiUrl + '/2fa/qrcode/' + this.profile.user_secret;
        },
    }
}
</script>
